// Persistent session socket + tiny pub/sub. Online = this socket is open.
// pause()/resume() (inactive players, spec 2026-10-22): an inactive player's socket is closed on
// purpose — the server's last-socket teardown then hides them from every room and tells their
// friends they went offline — and reopened when they come back; `subscribed` survives, so onopen
// re-joins the same rooms exactly as after any dropped connection.
export class Realtime {
  constructor(url, { WS, setTimer = (fn, ms) => setTimeout(fn, ms), clearTimer = (t) => clearTimeout(t),
                     now = () => performance.now() } = {}) {
    this.url = url;
    this._now = now;                  // a move's send time (`ts`): peers space our moves by it (ui/packetClock.mjs)
    this._WSctor = WS;                // tests pass a fake; the browser uses the global WebSocket
    this._setTimer = setTimer;
    this._clearTimer = clearTimer;
    this.ws = null;
    this.handlers = new Map();      // type -> Set<fn>
    this.subscribed = new Set();    // rooms we want to be in (re-sent on reconnect)
    this._backoff = 1000;
    this._retry = null;             // pending reconnect timer
    // Our last `move` in the current room, re-sent after every (re)subscribe on reconnect: the
    // server re-seeds a returning player at its default spot, and the controllers broadcast only
    // while moving — without this, others would see us there (standing) until we next walked.
    this._lastMove = null;
    this.paused = false;
  }

  get _WS() { return this._WSctor ?? globalThis.WebSocket; }

  connect() {
    if (this.paused) return;        // inactive: stay closed until resume()
    const WS = this._WS;
    if (this.ws && (this.ws.readyState === WS.OPEN || this.ws.readyState === WS.CONNECTING)) return;
    if (this._retry) { this._clearTimer(this._retry); this._retry = null; }
    const ws = new WS(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this._backoff = 1000;
      for (const room of this.subscribed) this._send({ type: 'subscribe', room });
      this._resendMove();
    };
    ws.onmessage = (ev) => {
      let msg; try { msg = JSON.parse(ev.data); } catch { return; }
      this._dispatch(msg);
    };
    ws.onclose = () => {
      this.ws = null;
      this._retry = this._setTimer(() => { this._retry = null; this.connect(); }, this._backoff);
      this._backoff = Math.min(this._backoff * 2, 15000);
    };
    ws.onerror = () => ws.close();
  }

  _dispatch(msg) {
    const set = this.handlers.get(msg.type);
    if (set) for (const fn of set) { try { fn(msg); } catch (e) { console.error(e); } }
  }

  // Close without an auto-reconnect: onclose is detached first and a pending retry cancelled.
  _close() {
    if (this._retry) { this._clearTimer(this._retry); this._retry = null; }
    const ws = this.ws;
    this.ws = null;
    if (ws) { ws.onclose = null; try { ws.close(); } catch { /* already closed */ } }
  }

  // The player went inactive: drop off the server (others stop seeing us) until resume().
  pause() {
    if (this.paused) return;
    this.paused = true;
    this._close();
    this._dispatch({ type: 'paused' });
  }

  // The player is back: reconnect at once; onopen re-subscribes every room in `subscribed`.
  resume() {
    if (!this.paused) return;
    this.paused = false;
    this._backoff = 1000;
    this.connect();
    this._dispatch({ type: 'resumed' });
  }

  // Tear down on logout: the socket is authenticated at handshake time, so it must not
  // survive into the next login. Detach onclose first so no auto-reconnect fires.
  disconnect() {
    this.paused = false;
    this._lastMove = null;
    this.subscribed.clear();
    this._close();
  }

  _send(obj) {
    if (this.ws && this.ws.readyState === this._WS.OPEN) this.ws.send(JSON.stringify(obj));
  }

  on(type, fn) {
    if (!this.handlers.has(type)) this.handlers.set(type, new Set());
    this.handlers.get(type).add(fn);
    return () => this.handlers.get(type)?.delete(fn);
  }

  // A room change forgets the last move: its coordinates belong to the room we left. A move made after
  // leaving is the next scene's arrival (a ride resumed in its create(), before it subscribes): it goes
  // again once we are in, or the others see the server's seed until we next move.
  subscribe(room) {
    this.subscribed.add(room);
    this._send({ type: 'subscribe', room });
    this._resendMove();
  }
  // Sent again now, so it goes with now's time: the old stamp would place it in the past.
  _resendMove() { if (this._lastMove) this._send({ ...this._lastMove, ts: this._now() }); }
  unsubscribe(room) { this._lastMove = null; this.subscribed.delete(room); this._send({ type: 'unsubscribe', room }); }
  move(p)           { this._lastMove = { type: 'move', ...p }; this._send({ ...this._lastMove, ts: this._now() }); }
  chat(text)        { this._send({ type: 'chat', text }); }
  outfit(payload)   { this._send({ type: 'outfit', outfit: payload }); }
  idle(flag)        { this._send({ type: 'idle', idle: !!flag }); }
  // farm activity everyone on the farm sees (farm/farmFx.mjs): job start / progress / end, pet, feed, water
  farmFx(p)         { this._send({ type: 'farm_fx', ...p }); }

  npcInteract(id)   { this._send({ type: 'npc_interact', id }); }
  npcRelease(id)    { this._send({ type: 'npc_release', id }); }

  tableSit(tableId, seat) { this._send({ type: 'table_sit', table_id: tableId, seat }); }
  tableStand()            { this._send({ type: 'table_stand' }); }
  tableSelectGame(gameId) { this._send({ type: 'table_select_game', game_id: gameId }); }
  tableClearGame()        { this._send({ type: 'table_clear_game' }); }

  // --- Game Hall match verbs (Plan 3, Task 4) ---
  tableAddBot(difficulty = 1) { this._send({ type: 'table_add_bot', difficulty }); }
  tableRemoveBot()            { this._send({ type: 'table_remove_bot' }); }
  tableStart()                { this._send({ type: 'table_start' }); }
  matchMove(move)             { this._send({ type: 'match_move', move }); }
  matchResign()               { this._send({ type: 'match_resign' }); }
  matchClaimWin()             { this._send({ type: 'match_claim_win' }); }
  // --- Cars (spec 2026-08-28) ---
  // Boarding is passenger-only: the driver is whoever broadcasts `move` with mode 'car'.
  // The server answers a refused board with `car_error` (see vehiclePresence.js).
  carBoard(carId) { this._send({ type: 'car_board', car_id: carId }); }
  carLeave()      { this._send({ type: 'car_leave' }); }
  carKick()       { this._send({ type: 'car_kick' }); }

  // --- Draw offers (Plan 4, Task 2 — game-agnostic) ---
  matchOfferDraw()             { this._send({ type: 'match_offer_draw' }); }
  matchRespondDraw(accept)     { this._send({ type: 'match_respond_draw', accept: !!accept }); }
}
